/*
 * Copyright (c) SEQTA Software 2018.
 *
 * Created by simon on 2018-05-29
 */

const POSSIBLE_ITEM_HEADERS = {
	code: 1,
	area_code: 1,
	area_name: 1,
	name: 1,
	details: 1,
	strand: 1
};

const POSSIBLE_CONTENT_HEADERS = {
	item_code: 1,
	code: 1,
	year: 1,
	contents: 1,
	context: 1
};

const QUOTE_REGEX = /(^"|"$)/g; // Remove quotes at start/end of string

/**
 * Trim, unquote
 */
export const cleanUpCell = cell => cell && cell.trim().replace(QUOTE_REGEX, '');

/**
 * Adapted from https://stackoverflow.com/questions/8493195/how-can-i-parse-a-csv-string-with-javascript-which-contains-comma-in-data
 * @param text CSV string
 * @returns {*[]} CSV rows
 */
const csvToArray = text => {
	let prevChar = '', row = [''], rows = [row], charIndex = 0, rowIndex = 0, isQuoteOpen = !0, thisChar;
	
	for (thisChar of text) {
		if ('"' === thisChar) {
			if (isQuoteOpen && thisChar === prevChar) row[charIndex] += thisChar; // Two double quotes in row inside open quotes is an escaped quote
			isQuoteOpen = !isQuoteOpen;
		} else if (',' === thisChar && isQuoteOpen) {
			thisChar = row[++charIndex] = '';
		} else if ('\n' === thisChar && isQuoteOpen) {
			if ('\r' === prevChar) row[charIndex] = row[charIndex].slice(0, -1);
			row = rows[++rowIndex] = [thisChar = '']; charIndex = 0;
		} else {
			row[charIndex] += thisChar;
		}
		prevChar = thisChar;
	}
	
	return rows;
};

const lineendRegex = /\n|\r/g;

/**
 * Convert the raw CSV string for an items upload into a usable array of key/value pairs, ready for upload to the
 * back-end.
 * @param csv String
 * @return {object} Object containing error prop if an error occurred, or items prop if parsing succeeded.
 */
export const parseItems = csv => {
	const lines = csv.split(lineendRegex);
	
	if (!lines[0].includes('code') || !lines[0].includes('area_code') || !lines[0].includes('area_name') || !lines[0].includes('name')) {
		return {
			error: `'code', 'area_code', 'area_name', and 'name' headers are required`
		};
	}
	
	// Grab the headers and strip out the quotes
	const headers = lines[0].split(',').map(cleanUpCell);
	
	if (headers.find(header => !POSSIBLE_ITEM_HEADERS[header])) {
		return {
			error: `There are unrecognised headers. Please limit to 'code', 'area_code', 'area_name', 'name', 'details', and 'strand'`
		};
	}
	
	if (
		headers.filter(col => col === 'code').length > 1 ||
		headers.filter(col => col === 'area_code').length > 1 ||
		headers.filter(col => col === 'area_name').length > 1 ||
		headers.filter(col => col === 'name').length > 1 ||
		headers.filter(col => col === 'details').length > 1 ||
		headers.filter(col => col === 'strand').length > 1
	) {
		return {
			error: `There are duplicate column headers`
		}
	}
	
	const items = lines.filter((line, index) => index !== 0 && line.length > 0).map(line => {
		let obj = {};
		const data = csvToArray(line)[0];
		for (let i = 0; i < headers.length; i++) {
			// Assign to the appropriate header and strip out the quotes
			obj[headers[i]] = cleanUpCell(data[i]);
		}
		return obj;
	});
	
	return {items};
};

/**
 * Convert the raw CSV string for a content upload into a usable array of key/value pairs, ready for upload to the
 * back-end.
 * @param csv String
 * @return {object} Object containing error prop if an error occurred, or content prop if parsing succeeded.
 */
export const parseContent = csv => {
	const lines = csv.split('\n');
	
	if (!lines[0].includes('code') || !lines[0].includes('item_code')) {
		return {
			error: `'code' and 'item_code' headers are required`
		};
	}
	
	// Grab the headers and strip out the quotes
	const headers = lines[0].split(',').map(cleanUpCell);
	
	if (headers.find(header => !POSSIBLE_CONTENT_HEADERS[header])) {
		return {
			error: `There are unrecognised headers. Please limit to 'item_code', 'code', 'year', 'contents', and 'context'`
		};
	}
	
	if (
		headers.filter(col => col === 'item_code').length > 1 ||
		headers.filter(col => col === 'code').length > 1 ||
		headers.filter(col => col === 'year').length > 1 ||
		headers.filter(col => col === 'contents').length > 1 ||
		headers.filter(col => col === 'context').length > 1
	) {
		return {
			error: `There are duplicate column headers`
		}
	}
	
	const content = lines.filter((line, index) => index !== 0 && line.length > 0).map((line, index) => {
		let obj = {
			line: index + 1
		};
		const data = csvToArray(line)[0];
		for (let i = 0; i < headers.length; i++) {
			// Assign to the appropriate header and strip out the quotes
			obj[headers[i]] = cleanUpCell(data[i]);
		}
		return obj;
	});
	
	return {content};
};



// WEBPACK FOOTER //
// ./src/syllabus/csvParser.js